import { useEffect, useState } from 'react';
import {
  useSearchParams,
  createSearchParams,
  useNavigate,
} from 'react-router-dom';
import App from './App';
import ConfigContext from './contexts/ConfigContext';
import { getConfigFileNameForLsid, getCountryCode } from './helpers';
import devSubDomains from './data/devSubDomains';
import { enphaseCustomer } from './v2/data/pages';
import globalCountries from './data/globalCountries';

export default function LoadApp() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentParams = Object.fromEntries(searchParams.entries());
  const [configData, setConfigData]: [any, any] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const isDebrandedVersion =
    process.env.REACT_APP_USE_DEBRANDED_VERSION === 'true' ||
    currentParams.version === 'edb';

  useEffect(() => {
    if (
      process.env.REACT_APP_ENV === 'local' &&
      !currentParams.a &&
      !currentParams.lsid &&
      !currentParams.lang
    ) {
      currentParams.lsid = '305';
      navigate({
        pathname: window.location.pathname,
        search: `?${createSearchParams(currentParams)}`,
        hash: window.location.hash,
      });
    }

    const loadData = async () => {
      if (
        window.countryData &&
        (process.env.REACT_APP_ENV === 'local' ||
          (process.env.REACT_APP_ENV === 'production' && window.testsConfig))
      ) {
        if (
          currentParams.source &&
          currentParams.source === 'social' &&
          window.countryData.country &&
          typeof window.countryData.country === 'string' &&
          window.countryData.country.length === 2 &&
          window.countryData.country !== 'US'
        ) {
          window.location.href = 'https://enphase.com/homeowners';
          return;
        }
        if (!currentParams.lang) {
          const appVersion = isDebrandedVersion ? 'edb' : 'enph';
          const currentSubDomain = window.location.hostname.split('.')[0];
          const isDevSubDomain = devSubDomains.some((subDomain: string) =>
            currentSubDomain.startsWith(subDomain)
          );
          let testsConfig: any;
          if (process.env.REACT_APP_ENV === 'local') {
            const configEnvironment = isDevSubDomain ? 'dev' : 'prod';
            const testsConfigFileUrl = `https://enph-funnel.s3.amazonaws.com/ab_tests_config/${configEnvironment}/${appVersion}.json?v=${Date.now()}`;
            const testsConfigData = await fetch(testsConfigFileUrl);
            testsConfig = await testsConfigData.json();
          } else {
            testsConfig = window.testsConfig;
          }

          if (isDevSubDomain) {
            console.log('AB tests config');
            console.log(testsConfig);
          }

          const configFileInfo = getConfigFileNameForLsid(
            currentParams.lsid,
            testsConfig,
            currentParams.forceTest || currentParams.ft,
            !!currentParams.consent && currentParams.consent === 'yes'
          );

          if (isDevSubDomain) {
            console.log('Selected test:');
            console.log(configFileInfo);
          }

          const configFileName = configFileInfo.configFile;

          const configDataRequest = await fetch(
            `./config/tests/${appVersion}/${configFileName}.json`
          );
          const configDataResponse = await configDataRequest.json();

          // Set active features from url params
          if (currentParams.features !== undefined) {
            configDataResponse.features = {};
            const activeFeatures = currentParams.features.split(',');
            activeFeatures.forEach(
              (feature: string) => (configDataResponse.features[feature] = true)
            );
          }

          const updateConfigData = (newData: any) => {
            setConfigData(newData);
          };

          setConfigData({
            ...configDataResponse,
            isDebrandedVersion: configDataResponse.version === 'debranded',
            testName: configFileInfo.testName,
            configFile: configFileInfo.configFile,
            updateConfigData,
            isGlobal: false,
          });

          if (configDataResponse.features.isOrganicFunnel) {
            currentParams.page = enphaseCustomer.page;
            setSearchParams(currentParams);
          }
        } else {
          const country = getCountryCode(currentParams.lang, globalCountries);
          const emptyConfigData = {
            version: 'enphase',
            data: {
              formSlides: [],
              sandboxSlides: [],
              successPage: {},
            },
            features: {},
          };

          setConfigData({
            ...emptyConfigData,
            country,
            countryLanguage: currentParams.lang,
            isGlobal: true,
          });
        }

        setIsLoading(false);
      } else {
        setTimeout(loadData, 50);
      }
    };

    loadData();
  }, []);

  return (
    <>
      {isLoading && (
        <div className="fixed left-0 top-0 w-full h-full flex items-center">
          <span className="loader-dots"></span>
        </div>
      )}
      {!isLoading && (
        <ConfigContext.Provider value={configData}>
          <App useDebrandedVersion={isDebrandedVersion}></App>
        </ConfigContext.Provider>
      )}
    </>
  );
}
